import { createAuthenticatedGraphQLClient } from '../../auth/createAuthenticatedGraphQLClient';
import type { CanvassingSessionExportFilter } from '../../graphql/__generated__/graphql';
import type {
  CanvassingSessionExportDownloadQuery,
  CanvassingSessionExportDownloadQueryVariables,
} from '../../graphql/queries/canvassingSessionExportDownload.generated';
import type {
  CanvassingSessionExportQuery,
  CanvassingSessionExportQueryVariables,
} from '../../graphql/queries/canvassingSessionExport.generated';
import type { CanvassingSessionExportLatestQuery } from '../../graphql/queries/canvassingSessionExportLatest.generated';
import type {
  ExportCanvassingSessionsMutation,
  ExportCanvassingSessionsMutationVariables,
} from '../../graphql/queries/exportCanvassingSessions.generated';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import { GraphQLRequestError } from '../../graphql/routing/apiRouting';
import {
  CANVASSING_SESSION_EXPORT_DOCUMENT,
  CANVASSING_SESSION_EXPORT_DOWNLOAD_DOCUMENT,
  CANVASSING_SESSION_EXPORT_LATEST_DOCUMENT,
  EXPORT_CANVASSING_SESSIONS_DOCUMENT,
} from '../canvassingSessionDocuments';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';

export type CanvassingSessionExportItem = ExportCanvassingSessionsMutation['exportCanvassingSessions'];
export type CanvassingSessionExportDownload = CanvassingSessionExportDownloadQuery['canvassingSessionExportDownload'];

export type CanvassingSessionExportError = {
  code: string | null;
  uncertain: boolean;
};

export type StartCanvassingSessionExportOptions = {
  filter: CanvassingSessionExportFilter;
  signal: AbortSignal;
  fetchImpl?: CanvassingSessionFetchImpl;
};

const readExtensionCode = (value: unknown): string | null => {
  if (typeof value !== 'object' || value === null) return null;
  const extensions = Reflect.get(value, 'extensions');
  if (typeof extensions !== 'object' || extensions === null) return null;
  const code = Reflect.get(extensions, 'code');
  return typeof code === 'string' ? code : null;
};

export const getCanvassingSessionExportError = (error: unknown): CanvassingSessionExportError => {
  if (!(error instanceof GraphQLRequestError)) return { code: null, uncertain: true };

  const codes = (error.graphQLErrors ?? []).map(readExtensionCode);
  if (error.status === 403 || codes.includes('FORBIDDEN')) return { code: 'FORBIDDEN', uncertain: false };
  const code = codes.find((candidate): candidate is string => candidate !== null) ?? null;

  if (error.kind === 'graphql') return { code, uncertain: false };
  return {
    code,
    uncertain: error.kind === 'empty-data' || error.status === undefined || error.status === 408 || error.status >= 500,
  };
};

export const canvassingSessionExportErrorCopy = (error: CanvassingSessionExportError): string => {
  if (error.uncertain) {
    return 'The export may have started, but its result could not be confirmed. Check the latest export status before trying again.';
  }

  switch (error.code) {
    case 'FORBIDDEN':
      return 'You do not have permission to view canvassing sessions.';
    case 'SESSION_EXPORT_IN_PROGRESS':
      return 'An export is already running for this account on this server.';
    case 'SESSION_EXPORT_STORAGE_UNAVAILABLE':
    case 'STORAGE_UNAVAILABLE':
      return 'Export storage is unavailable.';
    case 'BAD_USER_INPUT':
      return 'One or more filters are invalid. Review the date range, canvasser, capture status, processing status, and sort filters.';
    case 'UNAVAILABLE':
    case 'SESSION_EXPORT_UNAVAILABLE':
    default:
      return 'The session export could not be started. Try again.';
  }
};

export const startCanvassingSessionExport = async ({
  filter,
  signal,
  fetchImpl,
}: StartCanvassingSessionExportOptions): Promise<CanvassingSessionExportItem> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const variables: ExportCanvassingSessionsMutationVariables = { filter };
  const response = await client.request<ExportCanvassingSessionsMutation>({
    document: EXPORT_CANVASSING_SESSIONS_DOCUMENT,
    variables,
    signal,
  });
  return response.exportCanvassingSessions;
};

export type GetCanvassingSessionExportOptions = {
  id: string;
  signal: AbortSignal;
  fetchImpl?: CanvassingSessionFetchImpl;
};

export const getCanvassingSessionExport = async ({
  id,
  signal,
  fetchImpl,
}: GetCanvassingSessionExportOptions): Promise<CanvassingSessionExportItem | null> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const variables: CanvassingSessionExportQueryVariables = { id };
  const response = await client.request<CanvassingSessionExportQuery>({
    document: CANVASSING_SESSION_EXPORT_DOCUMENT,
    variables,
    signal,
  });
  return response.canvassingSessionExport ?? null;
};

export type GetLatestCanvassingSessionExportOptions = {
  signal: AbortSignal;
  fetchImpl?: CanvassingSessionFetchImpl;
};

export const getLatestCanvassingSessionExport = async ({
  signal,
  fetchImpl,
}: GetLatestCanvassingSessionExportOptions): Promise<CanvassingSessionExportItem | null> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const response = await client.request<CanvassingSessionExportLatestQuery>({
    document: CANVASSING_SESSION_EXPORT_LATEST_DOCUMENT,
    signal,
  });
  return response.canvassingSessionExportLatest ?? null;
};

export const getCanvassingSessionExportDownload = async ({
  id,
  signal,
  fetchImpl,
}: GetCanvassingSessionExportOptions): Promise<CanvassingSessionExportDownload> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const variables: CanvassingSessionExportDownloadQueryVariables = { id };
  const response = await client.request<CanvassingSessionExportDownloadQuery>({
    document: CANVASSING_SESSION_EXPORT_DOWNLOAD_DOCUMENT,
    variables,
    signal,
  });
  return response.canvassingSessionExportDownload;
};
